import React, { useEffect, useState } from "react";
import { api, formatEUR, apiUrl } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileJson, Calendar, Filter, TrendingUp } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Exports() {
  const [date, setDate] = useState(todayISO());
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/exports/end-of-day?date=${date}&format=json`);
      setPreview(r.data);
    } catch (e) {
      toast.error("Errore caricamento anteprima");
      setPreview(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadPreview(); }, [date]);

  const downloadFile = async (fmt) => {
    try {
      const token = localStorage.getItem("velta_token");
      const url = apiUrl(`/exports/end-of-day?date=${date}&format=${fmt}`);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("download failed");

      if (fmt === "json") {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        triggerDownload(blob, `velta_export_${date}.json`);
      } else {
        const blob = await res.blob();
        triggerDownload(blob, `velta_export_${fmt}_${date}.csv`);
      }
      toast.success("Download avviato");
    } catch {
      toast.error("Errore download");
    }
  };

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // Riepilogo IVA scorporata
  const vatBreakdown = (() => {
    const totals = { '4': 0, '5': 0, '10': 0, '22': 0 };
    (preview?.orders || []).forEach(o => {
      const vb = o.vat_break || {};
      Object.entries(vb).forEach(([k, v]) => {
        const key = String(parseInt(parseFloat(k)));
        if (totals[key] !== undefined) totals[key] += v;
      });
    });
    return totals;
  })();

  const paymentBreakdown = (() => {
    const out = { cash: 0, card: 0, ticket: 0, mixed: 0 };
    (preview?.orders || []).forEach(o => {
      const m = o.payment_method || 'cash';
      if (out[m] !== undefined) out[m] += o.total || 0;
    });
    return out;
  })();

  return (
    <div className="p-8 lg:p-12">
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Esportazioni</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Export ERP fine giornata</h1>
        <p className="text-velta-mute">Esporta gli scontrini chiusi in formato compatibile con i tuoi sistemi gestionali (Conad, Camst, ERP Sistemi, custom).</p>
      </div>

      <div className="bg-white border border-velta-border rounded-md p-6 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Data esportazione</label>
            <div className="inline-flex items-center gap-2 bg-velta-bg border border-velta-border rounded-md px-3 h-12">
              <Calendar className="w-4 h-4 text-velta-mute" />
              <input
                data-testid="exp-date-picker"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="bg-transparent outline-none font-mono"
              />
            </div>
          </div>
          <button
            data-testid="exp-today-btn"
            onClick={() => setDate(todayISO())}
            className="h-12 px-4 border border-velta-border rounded-md hover:border-velta-ink font-medium"
          >
            Oggi
          </button>
        </div>
      </div>

      {/* Stats preview */}
      {loading ? (
        <div className="text-center text-velta-mute py-12">Caricamento…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-velta-border rounded-md p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Scontrini</div>
              <div data-testid="exp-stat-count" className="font-mono font-bold text-3xl tracking-tighter">{preview?.orders_count || 0}</div>
            </div>
            <div className="bg-white border border-velta-border rounded-md p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Totale</div>
              <div data-testid="exp-stat-total" className="font-mono font-bold text-3xl tracking-tighter">{formatEUR(preview?.orders_total || 0)}</div>
            </div>
            <div className="bg-white border border-velta-border rounded-md p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">IVA totale</div>
              <div className="font-mono font-bold text-xl tracking-tight">
                {formatEUR(Object.values(vatBreakdown).reduce((s, v) => s + v, 0))}
              </div>
              <div className="text-xs text-velta-mute mt-1">scorporata</div>
            </div>
            <div className="bg-white border border-velta-border rounded-md p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Pagamenti</div>
              <div className="text-xs space-y-0.5">
                <div className="flex justify-between"><span>Contanti</span><span className="font-mono">{formatEUR(paymentBreakdown.cash)}</span></div>
                <div className="flex justify-between"><span>Carta</span><span className="font-mono">{formatEUR(paymentBreakdown.card)}</span></div>
                <div className="flex justify-between"><span>Ticket</span><span className="font-mono">{formatEUR(paymentBreakdown.ticket)}</span></div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-velta-border rounded-md p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-velta-mute" />
              <h2 className="font-heading font-semibold">Scorporo IVA</h2>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {['4', '5', '10', '22'].map(rate => (
                <div key={rate} className="bg-velta-bg rounded-md p-3">
                  <div className="text-xs text-velta-mute mb-1">Imponibile {rate}%</div>
                  <div className="font-mono font-bold">{formatEUR(vatBreakdown[rate])}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Download buttons */}
          <div className="grid md:grid-cols-3 gap-4">
            <button
              data-testid="exp-dl-summary"
              onClick={() => downloadFile("summary_csv")}
              disabled={!preview?.orders_count}
              className="group bg-white border-2 border-velta-border rounded-md p-6 text-left hover:border-velta-ink disabled:opacity-40 transition-colors"
            >
              <FileSpreadsheet className="w-8 h-8 text-[#2F9E77] mb-3" strokeWidth={1.5} />
              <h3 className="font-heading font-semibold text-lg mb-1">Riepilogo CSV</h3>
              <p className="text-xs text-velta-mute mb-3">Una riga per scontrino: data, ora, numero, totale, scorporo IVA, pagamento.</p>
              <div className="inline-flex items-center gap-1 text-sm font-medium text-[#1E2D5B] group-hover:underline">
                <Download className="w-3.5 h-3.5" /> Scarica
              </div>
            </button>

            <button
              data-testid="exp-dl-detail"
              onClick={() => downloadFile("detail_csv")}
              disabled={!preview?.orders_count}
              className="group bg-white border-2 border-velta-border rounded-md p-6 text-left hover:border-velta-ink disabled:opacity-40 transition-colors"
            >
              <FileSpreadsheet className="w-8 h-8 text-[#F59E0B] mb-3" strokeWidth={1.5} />
              <h3 className="font-heading font-semibold text-lg mb-1">Dettaglio articoli CSV</h3>
              <p className="text-xs text-velta-mute mb-3">Una riga per articolo venduto: categoria, articolo, qtà, prezzo, IVA, peso (per pesati).</p>
              <div className="inline-flex items-center gap-1 text-sm font-medium text-[#1E2D5B] group-hover:underline">
                <Download className="w-3.5 h-3.5" /> Scarica
              </div>
            </button>

            <button
              data-testid="exp-dl-json"
              onClick={() => downloadFile("json")}
              disabled={!preview?.orders_count}
              className="group bg-white border-2 border-velta-border rounded-md p-6 text-left hover:border-velta-ink disabled:opacity-40 transition-colors"
            >
              <FileJson className="w-8 h-8 text-[#7C3AED] mb-3" strokeWidth={1.5} />
              <h3 className="font-heading font-semibold text-lg mb-1">JSON completo</h3>
              <p className="text-xs text-velta-mute mb-3">Dump completo strutturato per API/integrazioni ERP custom.</p>
              <div className="inline-flex items-center gap-1 text-sm font-medium text-[#1E2D5B] group-hover:underline">
                <Download className="w-3.5 h-3.5" /> Scarica
              </div>
            </button>
          </div>

          {/* Recent orders preview */}
          {preview?.orders_count > 0 && (
            <div className="mt-8 bg-white border border-velta-border rounded-md overflow-hidden">
              <div className="px-6 py-4 border-b border-velta-border flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-velta-mute" />
                <h2 className="font-heading font-semibold">Anteprima ({preview.orders.length} scontrini)</h2>
              </div>
              <div className="overflow-auto max-h-96">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-velta-bg sticky top-0">
                    <tr className="text-xs text-velta-mute uppercase tracking-widest">
                      <th className="text-left px-4 py-2">Ora</th>
                      <th className="text-left px-4 py-2">N°</th>
                      <th className="text-left px-4 py-2">Modalità</th>
                      <th className="text-left px-4 py-2">Pagamento</th>
                      <th className="text-right px-4 py-2">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(preview.orders) ? preview.orders : []).map(o => (
                      <tr key={o.id} className="border-t border-velta-border hover:bg-velta-bg">
                        <td className="px-4 py-2 font-mono text-xs">{(o.closed_at || '').slice(11, 19)}</td>
                        <td className="px-4 py-2 font-mono">{o.receipt_number || o.order_number || '—'}</td>
                        <td className="px-4 py-2 capitalize">{o.mode}</td>
                        <td className="px-4 py-2 text-xs">{o.payment_method || 'cash'}</td>
                        <td className="px-4 py-2 text-right font-mono font-medium">{formatEUR(o.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {preview?.orders_count === 0 && (
            <div className="mt-6 bg-white border border-velta-border rounded-md p-12 text-center text-velta-mute">
              Nessuno scontrino chiuso in questa data.
            </div>
          )}
        </>
      )}
    </div>
  );
}
